import React, { useEffect, useState, useCallback } from "react";
import { Loader2, TrendingUp, ShieldAlert, Users, CircleDollarSign, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import AdminAffiliatePayouts from "@/pages/AdminAffiliatePayouts";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";

function DashboardTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        setLoading(true);
        try { setData(await apiFetch("/admin/affiliate/dashboard")); }
        catch (e) { toast.error(e.message); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (loading || !data) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[var(--color-primary)]" /></div>;

    const kpis = [
        { label: "Active affiliates", value: data.affiliates_active_count, icon: Users, testid: "aff-kpi-active" },
        { label: "Revenue MTD (credits)", value: data.revenue_credits_mtd.toLocaleString(), icon: TrendingUp, testid: "aff-kpi-revenue" },
        { label: "Commissions paid MTD ($)", value: data.commissions_paid_mtd_eur.toFixed(2), icon: CircleDollarSign, testid: "aff-kpi-comm-paid" },
        { label: "Pending payouts ($)", value: data.pending_payouts_total_eur.toFixed(2), icon: CircleDollarSign, testid: "aff-kpi-pending" },
        { label: "Open fraud alerts", value: data.fraud_alerts_open, icon: ShieldAlert, testid: "aff-kpi-fraud" },
    ];

    return (
        <div className="space-y-6" data-testid="affiliate-dashboard">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                {kpis.map((k) => (
                    <div key={k.label} data-testid={k.testid} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-1">
                            <k.icon className="h-3 w-3" /> {k.label}
                        </div>
                        <div className="text-xl font-semibold text-text-main tabular-nums">{k.value}</div>
                    </div>
                ))}
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
                    <h3 className="text-sm font-semibold mb-3">Top affiliates MTD</h3>
                    {data.top_affiliates_mtd.length === 0 ? <p className="text-xs text-text-secondary">No conversions this month.</p> :
                    <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-widest text-text-secondary">
                            <tr>
                                <th className="text-left py-1">User</th>
                                <th className="text-right py-1">Revenue (c)</th>
                                <th className="text-right py-1">Comm ($)</th>
                                <th className="text-right py-1">Conv</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.top_affiliates_mtd.map((r, i) => (
                                <tr key={i} className="border-t border-[var(--color-border)]/40">
                                    <td className="py-1.5 text-text-main truncate max-w-[200px]">{r.user_email || r.user_id}</td>
                                    <td className="py-1.5 text-right tabular-nums">{r.revenue_credits.toLocaleString()}</td>
                                    <td className="py-1.5 text-right tabular-nums">{r.commission_eur?.toFixed?.(2) ?? "0.00"}</td>
                                    <td className="py-1.5 text-right tabular-nums text-text-secondary">{r.conversions}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>}
                </div>
                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
                    <h3 className="text-sm font-semibold mb-3">Traffic (30d)</h3>
                    <div className="space-y-2 text-xs">
                        <div className="flex justify-between"><span className="text-text-secondary">Clicks</span><span className="tabular-nums">{data.traffic_last_30d.clicks.toLocaleString()}</span></div>
                        <div className="flex justify-between"><span className="text-text-secondary">Unique visitors</span><span className="tabular-nums">{data.traffic_last_30d.unique_visitors.toLocaleString()}</span></div>
                        <div className="flex justify-between"><span className="text-text-secondary">Conversions</span><span className="tabular-nums">{data.traffic_last_30d.conversions.toLocaleString()}</span></div>
                        <div className="flex justify-between border-t border-[var(--color-border)]/40 pt-2 mt-2">
                            <span className="text-text-secondary">Conv rate</span>
                            <span className="tabular-nums text-[var(--color-primary)]">{(data.traffic_last_30d.conversion_rate * 100).toFixed(2)}%</span>
                        </div>
                    </div>
                </div>
            </div>

            <button onClick={load} data-testid="affiliate-dashboard-refresh" className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-1.5 inline-flex items-center gap-1">
                <RefreshCw className="h-3 w-3" /> Refresh
            </button>
        </div>
    );
}

function FraudAlertsTab() {
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState("open");
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await apiFetch(`/admin/affiliate/fraud-alerts?status=${status}`);
            setRows(r.items || []);
        } catch (e) { toast.error(e.message); }
        finally { setLoading(false); }
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const dismiss = async (id) => {
        const reason = prompt("Dismiss reason:") || "";
        try {
            await apiFetch(`/admin/affiliate/fraud-alerts/${id}/dismiss`, { method: "POST", body: JSON.stringify({ reason }) });
            toast.success("Dismissed.");
            await load();
        } catch (e) { toast.error(e.message); }
    };
    const suspend = async (id) => {
        const reason = prompt("Suspension reason:");
        if (!reason) return;
        try {
            await apiFetch(`/admin/affiliate/fraud-alerts/${id}/suspend-affiliate`, { method: "POST", body: JSON.stringify({ reason }) });
            toast.success("Affiliate suspended.");
            await load();
        } catch (e) { toast.error(e.message); }
    };

    return (
        <div data-testid="affiliate-fraud-alerts">
            <div className="flex items-center gap-2 mb-4">
                {["open", "dismissed", "actioned"].map((s) => (
                    <button key={s} onClick={() => setStatus(s)} data-testid={`aff-fraud-status-${s}`} className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full ${status === s ? "bg-white hover:bg-[#EDEDED] text-black" : "border border-[var(--color-border)] text-text-secondary hover:text-text-main"}`}>{s}</button>
                ))}
            </div>
            {loading ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[var(--color-primary)]" /></div> :
            rows.length === 0 ? <p className="text-xs text-text-secondary">No {status} alerts.</p> :
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-widest text-text-secondary bg-[var(--color-panel-ai)]">
                        <tr>
                            <th className="text-left py-2 px-3">Type</th>
                            <th className="text-left py-2 px-3">Affiliate</th>
                            <th className="text-right py-2 px-3">Count</th>
                            <th className="text-left py-2 px-3">First seen</th>
                            <th className="text-left py-2 px-3">Last seen</th>
                            <th className="text-right py-2 px-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-[var(--color-border)]/60" data-testid={`aff-fraud-row-${r.id}`}>
                                <td className="py-2 px-3"><span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-[var(--color-alert)]/10 text-[var(--color-alert)] border border-[var(--color-alert)]/30">{r.type}</span></td>
                                <td className="py-2 px-3 text-text-secondary truncate max-w-[220px]"><code className="text-[10px]">{r.affiliate_user_id}</code></td>
                                <td className="py-2 px-3 text-right tabular-nums">{r.count || 1}</td>
                                <td className="py-2 px-3 text-text-secondary tabular-nums">{(r.first_seen || "").slice(0, 19).replace("T", " ")}</td>
                                <td className="py-2 px-3 text-text-secondary tabular-nums">{(r.last_seen || "").slice(0, 19).replace("T", " ")}</td>
                                <td className="py-2 px-3 text-right space-x-2">
                                    {status === "open" && (
                                        <>
                                            <button onClick={() => dismiss(r.id)} data-testid={`aff-fraud-dismiss-${r.id}`} className="text-[10px] rounded-full border border-[var(--color-border)] px-2 py-1 text-text-secondary hover:text-text-main">Dismiss</button>
                                            <button onClick={() => suspend(r.id)} data-testid={`aff-fraud-suspend-${r.id}`} className="text-[10px] rounded-full bg-[var(--color-alert)]/10 border border-[var(--color-alert)]/40 text-[var(--color-alert)] px-2 py-1">Suspend affiliate</button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>}
        </div>
    );
}

const TABS = [
    { key: "dashboard", label: "Dashboard" },
    { key: "payouts", label: "Payouts" },
    { key: "fraud", label: "Fraud Alerts" },
];

export default function AdminAffiliate() {
    const { user: me } = useAuth();
    const [tab, setTab] = useState("dashboard");

    if (!isAdminRole(me?.role)) return <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center"><h1 className="text-2xl">Admin only</h1></div>;

    // Payouts tab reuses the existing standalone page. Since it renders
    // its own SidebarLayout, we render it verbatim (no double sidebar
    // — the internal SidebarLayout wins) when its tab is active.
    if (tab === "payouts") {
        return (
            <div>
                <div className="fixed top-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 rounded-full bg-[var(--color-card)] border border-[var(--color-border)] px-3 py-1.5 shadow-lg">
                    {TABS.map((t) => (
                        <button key={t.key} data-testid={`aff-tab-${t.key}`} onClick={() => setTab(t.key)} className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full ${tab === t.key ? "bg-white hover:bg-[#EDEDED] text-black" : "text-text-secondary hover:text-text-main"}`}>{t.label}</button>
                    ))}
                </div>
                <AdminAffiliatePayouts />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main className="flex-1 p-6 md:p-8 max-w-6xl mx-auto w-full" data-testid="admin-affiliate">
                    <div className="mb-6">
                        <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">Affiliate program</div>
                        <h1 className="text-3xl md:text-4xl font-semibold">Affiliate control</h1>
                    </div>
                    <div className="flex items-center gap-2 mb-6">
                        {TABS.map((t) => (
                            <button key={t.key} data-testid={`aff-tab-${t.key}`} onClick={() => setTab(t.key)} className={`text-xs uppercase tracking-widest px-4 py-2 rounded-full ${tab === t.key ? "bg-white hover:bg-[#EDEDED] text-black" : "border border-[var(--color-border)] text-text-secondary hover:text-text-main"}`}>{t.label}</button>
                        ))}
                    </div>
                    {tab === "dashboard" ? <DashboardTab /> : tab === "fraud" ? <FraudAlertsTab /> : null}
                </main>
            </div>
        </div>
    );
}
